import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { useNavigate } from 'react-router-dom';
import TimeGanttBar, { TimeGanttLegend } from '../components/TimeGanttBar';
import { 
  Clock, 
  Upload, 
  RefreshCw,
  FileUp,
  CheckCircle,
  AlertTriangle,
  XCircle,
  FileText,
  ClipboardList,
  Check,
  Trash2,
  Filter,
  AlertCircle,
  Maximize2,
  Minimize2,
  CheckCircle2,
  Clock3,
  Eye,
  Calculator,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  User,
  Sparkles,
  Brain,
  Database,
  Calendar,
  FileSpreadsheet,
  X,
  Zap,
  StickyNote,
  RotateCcw,
  Plus,
  Paperclip,
  ExternalLink,
  DollarSign
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';

const TimesheetsPage = () => {
  const navigate = useNavigate();
  // Batch state
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullScreenMode, setFullScreenMode] = useState(false);
  
  // Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [batchName, setBatchName] = useState('');
  const [importMode, setImportMode] = useState('xlsx');
  const [excelFile, setExcelFile] = useState(null);
  const [timesheetFile, setTimesheetFile] = useState(null);
  const [docketFile, setDocketFile] = useState(null);
  
  // Validation state
  const [validationRecords, setValidationRecords] = useState([]);
  const [validationFilter, setValidationFilter] = useState('all');
  const [loadingValidation, setLoadingValidation] = useState(false);
  const [validationTotals, setValidationTotals] = useState({}); // { employee_id: {total_hours, ordinary_hours, ...} }
  
  // Resolution dialog state
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [resolutionSource, setResolutionSource] = useState('timesheet');
  const [manualStart, setManualStart] = useState('');
  const [manualEnd, setManualEnd] = useState('');
  const [manualBreak, setManualBreak] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolving, setResolving] = useState(false);
  
  // AI suggestion state
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAiSuggestion, setLoadingAiSuggestion] = useState(false);
  
  // Employee grouping state
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());
  const [groupByEmployee, setGroupByEmployee] = useState(true);
  
  // Payroll processing state
  const [processingPayroll, setProcessingPayroll] = useState(null);

  // Manual Import state (from SQL stored procedure)
  const [showManualImportDialog, setShowManualImportDialog] = useState(false);
  const [manualImportWeek, setManualImportWeek] = useState('');
  const [fetchingManualData, setFetchingManualData] = useState(false);
  const [manualPreviewData, setManualPreviewData] = useState(null);
  const [manualPreviewModalOpen, setManualPreviewModalOpen] = useState(false);
  const [confirmingManualImport, setConfirmingManualImport] = useState(false);
  const [manualImportSuccessData, setManualImportSuccessData] = useState(null);
  const [manualImportSuccessModalOpen, setManualImportSuccessModalOpen] = useState(false);

  // Docket Items modal state
  const [docketItemsModalOpen, setDocketItemsModalOpen] = useState(false);
  const [selectedDocketNumber, setSelectedDocketNumber] = useState(null);
  const [docketItems, setDocketItems] = useState([]);
  const [loadingDocketItems, setLoadingDocketItems] = useState(false);

  // Bulk resolve state
  const [bulkResolving, setBulkResolving] = useState(null); // employee_id being bulk resolved
  const [scanningAllowances, setScanningAllowances] = useState(false);
  const [undoingEmployee, setUndoingEmployee] = useState(null); // employee_id being bulk undone

  // Employee note state (per-batch)
  const [employeeNotes, setEmployeeNotes] = useState({}); // { employee_id: note }
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [notePopoverOpen, setNotePopoverOpen] = useState(null); // employee_id currently editing

  // Validation allowance state (per-day, flows into payroll)
  const [allowanceDialogOpen, setAllowanceDialogOpen] = useState(false);
  const [allowanceRecord, setAllowanceRecord] = useState(null);
  const [availableAllowances, setAvailableAllowances] = useState([]);
  const [noteSuggestions, setNoteSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [allowanceName, setAllowanceName] = useState('');
  const [allowanceAmount, setAllowanceAmount] = useState('');
  const [allowanceUnit, setAllowanceUnit] = useState('per day');
  const [allowanceCode, setAllowanceCode] = useState('MANUAL');
  const [addingAllowance, setAddingAllowance] = useState(false);
  const [editingAllowance, setEditingAllowance] = useState(null); // allowance being edited

  // Bulk "Scan notes for allowances" state
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanning, setScanning] = useState(null); // employee_id being scanned
  const [scanResults, setScanResults] = useState([]);
  const [scanEmployeeName, setScanEmployeeName] = useState('');
  const [scanSelections, setScanSelections] = useState({}); // key `${record_id}::${i}` -> bool
  const [applyingScan, setApplyingScan] = useState(false);

  // Fetch batches
  const fetchBatches = async () => {
    try {
      const response = await API.get('/batches');
      setBatches(response.data);
    } catch (error) {
      toast.error('Failed to fetch import batches');
    } finally {
      setLoading(false);
    }
  };

  // Fetch validation records for selected batch
  const fetchValidation = useCallback(async (batchId) => {
    setLoadingValidation(true);
    try {
      const filterParam = validationFilter !== 'all' ? `?status_filter=${validationFilter}` : '';
      const response = await API.get(`/batches/${batchId}/validation${filterParam}`);
      setValidationRecords(response.data);
    } catch (error) {
      toast.error('Failed to fetch validation records');
    } finally {
      setLoadingValidation(false);
    }
  }, [validationFilter]);

  const fetchValidationTotals = useCallback(async (batchId) => {
    try {
      const response = await API.get(`/batches/${batchId}/validation-totals`);
      setValidationTotals(response.data?.totals || {});
    } catch (error) {
      setValidationTotals({});
    }
  }, []);

  const handleScanNoteAllowances = async () => {
    if (!selectedBatch || scanningAllowances) return;
    setScanningAllowances(true);
    try {
      const res = await API.post(`/batches/${selectedBatch.id}/parse-note-allowances`);
      const added = res.data?.allowances_added ?? 0;
      if (added > 0) {
        toast.success(`Auto-added ${added} allowance${added !== 1 ? 's' : ''} found in notes`);
      } else {
        toast.info('No new dollar allowances found in notes');
      }
      await fetchValidation(selectedBatch.id);
      fetchValidationTotals(selectedBatch.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan notes for allowances');
    } finally {
      setScanningAllowances(false);
    }
  };

  const fetchEmployeeNotes = useCallback(async (batchId) => {
    try {
      const response = await API.get(`/batches/${batchId}/employee-notes`);
      setEmployeeNotes(response.data || {});
    } catch (error) {
      setEmployeeNotes({});
    }
  }, []);

  const saveEmployeeNote = async (employeeId) => {
    if (!selectedBatch) return;
    setSavingNote(true);
    try {
      const response = await API.put(`/batches/${selectedBatch.id}/employee-notes`, {
        employee_id: employeeId,
        note: noteDraft
      });
      setEmployeeNotes(prev => {
        const next = { ...prev };
        if (response.data.note) next[employeeId] = response.data.note;
        else delete next[employeeId];
        return next;
      });
      toast.success(response.data.note ? 'Note saved' : 'Note cleared');
      setNotePopoverOpen(null);
    } catch (error) {
      toast.error('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  // Only fetch validation when batch ID changes (not on stats updates)
  const selectedBatchId = selectedBatch?.id;
  useEffect(() => {
    if (selectedBatchId) {
      fetchValidation(selectedBatchId);
      fetchEmployeeNotes(selectedBatchId);
      fetchValidationTotals(selectedBatchId);
    }
  }, [selectedBatchId, fetchValidation, fetchEmployeeNotes, fetchValidationTotals]);

  // Keep the per-employee header totals in sync after any validation change (debounced)
  useEffect(() => {
    if (!selectedBatchId || validationRecords.length === 0) return;
    const t = setTimeout(() => fetchValidationTotals(selectedBatchId), 600);
    return () => clearTimeout(t);
  }, [validationRecords, selectedBatchId, fetchValidationTotals]);

  // File dropzone handlers
  const onDropExcel = useCallback((acceptedFiles) => {
    setExcelFile(acceptedFiles[0]);
  }, []);

  const onDropTimesheet = useCallback((acceptedFiles) => {
    setTimesheetFile(acceptedFiles[0]);
  }, []);

  const onDropDocket = useCallback((acceptedFiles) => {
    setDocketFile(acceptedFiles[0]);
  }, []);

  const { getRootProps: getExcelRootProps, getInputProps: getExcelInputProps, isDragActive: isExcelDragActive } = useDropzone({
    onDrop: onDropExcel,
    accept: { 
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1
  });

  const { getRootProps: getTsRootProps, getInputProps: getTsInputProps, isDragActive: isTsDragActive } = useDropzone({
    onDrop: onDropTimesheet,
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1
  });

  const { getRootProps: getDkRootProps, getInputProps: getDkInputProps, isDragActive: isDkDragActive } = useDropzone({
    onDrop: onDropDocket,
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1
  });

  // Import handler
  const handleImport = async () => {
    if (!batchName.trim()) {
      toast.error('Please enter a batch name');
      return;
    }
    
    if (importMode === 'xlsx') {
      if (!excelFile) {
        toast.error('Please select an Excel file');
        return;
      }
    } else {
      if (!timesheetFile || !docketFile) {
        toast.error('Please select both CSV files');
        return;
      }
    }

    setImporting(true);
    const formData = new FormData();
    
    if (importMode === 'xlsx') {
      formData.append('excel_file', excelFile);
    } else {
      formData.append('timesheet_file', timesheetFile);
      formData.append('docket_file', docketFile);
    }

    try {
      const response = await API.post(`/batches/import?batch_name=${encodeURIComponent(batchName)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Import complete: ${response.data.timesheet_count} timesheets, ${response.data.docket_count} dockets`);
      
      // Reset form and close dialog
      setBatchName('');
      setExcelFile(null);
      setTimesheetFile(null);
      setDocketFile(null);
      setShowImportDialog(false);
      
      // Refresh batches
      await fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Approve batch handler
  const handleApproveBatch = async (batchId) => {
    try {
      await API.put(`/batches/${batchId}/approve`);
      toast.success('Batch approved');
      fetchBatches();
      if (selectedBatch?.id === batchId) {
        const updated = await API.get(`/batches/${batchId}`);
        setSelectedBatch(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve batch');
    }
  };

  // Process Payroll handler
  const handleProcessPayroll = async (batchId) => {
    setProcessingPayroll(batchId);
    try {
      const response = await API.post(`/payroll/process/${batchId}`);
      toast.success(response.data.message || 'Payroll processed successfully');
      fetchBatches();
      // Navigate to payroll page
      navigate('/payroll');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payroll');
    } finally {
      setProcessingPayroll(null);
    }
  };

  // Resolution handlers
  const openResolveDialog = async (record) => {
    setSelectedRecord(record);
    setResolutionSource('timesheet');
    setManualStart(record.ts_start_time || record.dk_start_time || '');
    setManualEnd(record.ts_end_time || record.dk_end_time || '');
    setManualBreak(String(record.ts_break_minutes || record.dk_break_minutes || 0));
    setResolutionNotes('');
    setAiSuggestion(null);
    setResolveDialogOpen(true);
    
    // Fetch AI suggestion
    fetchAiSuggestion(record);
  };
  
  const fetchAiSuggestion = async (record) => {
    setLoadingAiSuggestion(true);
    try {
      // Calculate time differences
      const calcDiff = (t1, t2) => {
        if (!t1 || !t2) return null;
        const [h1, m1] = t1.split(':').map(Number);
        const [h2, m2] = t2.split(':').map(Number);
        return (h1 * 60 + m1) - (h2 * 60 + m2);
      };
      
      const startDiff = calcDiff(record.ts_start_time, record.dk_start_time);
      const endDiff = calcDiff(record.ts_end_time, record.dk_end_time);
      
      const params = new URLSearchParams();
      if (record.employee_id) params.append('employee_id', record.employee_id);
      if (record.site_id) params.append('site_id', record.site_id);
      if (record.discrepancy_types?.[0]) params.append('discrepancy_type', record.discrepancy_types[0]);
      if (startDiff !== null) params.append('start_diff', startDiff);
      if (endDiff !== null) params.append('end_diff', endDiff);
      
      const response = await API.get(`/ai/resolution-suggestion?${params}`);
      setAiSuggestion(response.data);
    } catch (error) {
      console.error('Failed to fetch AI suggestion:', error);
      setAiSuggestion(null);
    } finally {
      setLoadingAiSuggestion(false);
    }
  };
  
  const applyAiSuggestion = () => {
    if (aiSuggestion?.suggested_source) {
      setResolutionSource(aiSuggestion.suggested_source);
    }
  };

  const handleResolve = async () => {
    if (!selectedRecord) return;
    
    const recordId = selectedRecord.id;
    
    // OPTIMISTIC UPDATE: Immediately update local state
    setValidationRecords(prev => prev.map(record => 
      record.id === recordId 
        ? { ...record, status: 'resolved', resolution_source: resolutionSource }
        : record
    ));
    
    setResolving(true);
    setResolveDialogOpen(false);
    
    try {
      let url = `/validation/${recordId}/resolve?resolution_source=${resolutionSource}`;
      if (resolutionSource === 'manual') {
        url += `&manual_start=${encodeURIComponent(manualStart)}`;
        url += `&manual_end=${encodeURIComponent(manualEnd)}`;
        url += `&manual_break=${manualBreak}`;
      }
      if (resolutionNotes) {
        url += `&notes=${encodeURIComponent(resolutionNotes)}`;
      }
      
      await API.put(url);
      
      // Record this resolution for AI learning (fire and forget)
      API.post('/ai/resolution-patterns', {
        employee_id: selectedRecord.employee_id,
        employee_name: selectedRecord.employee_name,
        site_id: selectedRecord.site_id,
        site_name: selectedRecord.site_name,
        date: selectedRecord.date,
        discrepancy_types: selectedRecord.discrepancy_types || [],
        ts_start_time: selectedRecord.ts_start_time,
        ts_end_time: selectedRecord.ts_end_time,
        ts_break_minutes: selectedRecord.ts_break_minutes,
        dk_start_time: selectedRecord.dk_start_time,
        dk_end_time: selectedRecord.dk_end_time,
        dk_break_minutes: selectedRecord.dk_break_minutes,
        resolution_source: resolutionSource,
        final_start_time: resolutionSource === 'manual' ? manualStart : 
                         resolutionSource === 'timesheet' ? selectedRecord.ts_start_time : selectedRecord.dk_start_time,
        final_end_time: resolutionSource === 'manual' ? manualEnd :
                       resolutionSource === 'timesheet' ? selectedRecord.ts_end_time : selectedRecord.dk_end_time,
        final_break_minutes: resolutionSource === 'manual' ? parseInt(manualBreak) :
                            resolutionSource === 'timesheet' ? selectedRecord.ts_break_minutes : selectedRecord.dk_break_minutes,
      }).catch(() => {});
      
      toast.success('Discrepancy resolved');
      
      // Update batch stats locally (no API refresh)
      setSelectedBatch(prev => prev ? {
        ...prev,
        discrepancy_count: Math.max(0, (prev.discrepancy_count || 0) - 1),
        resolved_count: (prev.resolved_count || 0) + 1
      } : prev);
      
      setBatches(prev => prev.map(b => 
        b.id === selectedBatch.id 
          ? {
              ...b,
              discrepancy_count: Math.max(0, (b.discrepancy_count || 0) - 1),
              resolved_count: (b.resolved_count || 0) + 1
            }
          : b
      ));
    } catch (error) {
      // ROLLBACK: Revert the record back to discrepancy
      setValidationRecords(prev => prev.map(record => 
        record.id === recordId 
          ? { ...record, status: 'discrepancy', resolution_source: null }
          : record
      ));
      toast.error(error.response?.data?.detail || 'Failed to resolve');
    } finally {
      setResolving(false);
    }
  };

  const handleResolveAllMatched = async () => {
    if (!selectedBatch) return;
    
    // Get all matched records that will be resolved
    const matchedRecords = validationRecords.filter(r => r.status === 'matched');
    const matchedIds = matchedRecords.map(r => r.id);
    const matchedCount = matchedIds.length;
    
    if (matchedCount === 0) {
      toast.info('No matched records to resolve');
      return;
    }
    
    // OPTIMISTIC UPDATE: Immediately mark all matched as resolved
    setValidationRecords(prev => prev.map(record => 
      matchedIds.includes(record.id)
        ? { ...record, status: 'resolved' }
        : record
    ));
    
    try {
      const response = await API.post(`/batches/${selectedBatch.id}/resolve-matched`);
      toast.success(`Resolved ${response.data.resolved_count} matched records`);
      
      // Update batch stats locally (no API refresh)
      const resolvedCount = response.data.resolved_count || matchedCount;
      setSelectedBatch(prev => prev ? {
        ...prev,
        matched_count: Math.max(0, (prev.matched_count || 0) - resolvedCount),
        resolved_count: (prev.resolved_count || 0) + resolvedCount
      } : prev);
      
      setBatches(prev => prev.map(b => 
        b.id === selectedBatch.id 
          ? {
              ...b,
              matched_count: Math.max(0, (b.matched_count || 0) - resolvedCount),
              resolved_count: (b.resolved_count || 0) + resolvedCount
            }
          : b
      ));
    } catch (error) {
      // ROLLBACK: Revert matched records
      setValidationRecords(prev => prev.map(record => 
        matchedIds.includes(record.id)
          ? { ...record, status: 'matched' }
          : record
      ));
      toast.error('Failed to auto-resolve');
    }
  };

  // Bulk resolve all discrepancies for an employee using timesheet or docket times
  const handleBulkResolve = async (employeeId, source) => {
    if (!selectedBatch || bulkResolving) return;
    
    // Get all unresolved records for this employee
    const employeeRecords = validationRecords.filter(
      r => r.employee_id === employeeId && r.status === 'discrepancy'
    );
    
    if (employeeRecords.length === 0) {
      toast.info('No discrepancies to resolve');
      return;
    }
    
    const recordIds = employeeRecords.map(r => r.id);
    const recordCount = recordIds.length;
    
    // OPTIMISTIC UPDATE: Immediately update local state
    setValidationRecords(prev => prev.map(record => 
      recordIds.includes(record.id) 
        ? { ...record, status: 'resolved', resolution_source: source }
        : record
    ));
    
    setBulkResolving(employeeId);
    const failedIds = [];
    
    // Make API calls in background
    const resolvePromises = employeeRecords.map(async (record) => {
      try {
        const url = `/validation/${record.id}/resolve?resolution_source=${source}`;
        await API.put(url);
        
        // Record for AI learning (fire and forget)
        API.post('/ai/resolution-patterns', {
          employee_id: record.employee_id,
          employee_name: record.employee_name,
          site_id: record.site_id,
          site_name: record.site_name,
          date: record.date,
          discrepancy_types: record.discrepancy_types || [],
          ts_start_time: record.ts_start_time,
          ts_end_time: record.ts_end_time,
          ts_break_minutes: record.ts_break_minutes,
          dk_start_time: record.dk_start_time,
          dk_end_time: record.dk_end_time,
          dk_break_minutes: record.dk_break_minutes,
          resolution_source: source,
          final_start_time: source === 'timesheet' ? record.ts_start_time : record.dk_start_time,
          final_end_time: source === 'timesheet' ? record.ts_end_time : record.dk_end_time,
          final_break_minutes: source === 'timesheet' ? record.ts_break_minutes : record.dk_break_minutes,
        }).catch(() => {});
        
        return { success: true, id: record.id };
      } catch (e) {
        console.error(`Failed to resolve record ${record.id}:`, e);
        return { success: false, id: record.id };
      }
    });
    
    // Wait for all API calls to complete
    const results = await Promise.all(resolvePromises);
    const failures = results.filter(r => !r.success);
    
    if (failures.length > 0) {
      // ROLLBACK: Revert failed records back to discrepancy
      const failedRecordIds = failures.map(f => f.id);
      setValidationRecords(prev => prev.map(record =>
        failedRecordIds.includes(record.id)
          ? { ...record, status: 'discrepancy', resolution_source: null }
          : record
      ));
      toast.error(`Failed to resolve ${failures.length} of ${recordCount} entries`);
    }
    
    const successCount = recordCount - failures.length;
    if (successCount > 0) {
      toast.success(`Resolved ${successCount} entries using ${source} times`);
    }
    
    // Update batch stats locally (no full refresh)
    if (successCount > 0) {
      // Update the selected batch stats inline
      setSelectedBatch(prev => prev ? {
        ...prev,
        discrepancy_count: Math.max(0, (prev.discrepancy_count || 0) - successCount),
        resolved_count: (prev.resolved_count || 0) + successCount
      } : prev);
      
      // Update the batches list stats inline
      setBatches(prev => prev.map(b => 
        b.id === selectedBatch.id 
          ? {
              ...b,
              discrepancy_count: Math.max(0, (b.discrepancy_count || 0) - successCount),
              resolved_count: (b.resolved_count || 0) + successCount
            }
          : b
      ));
    }
    
    setBulkResolving(null);
  };

  // Compute the pre-resolution status of a record (mirrors backend _revert_status)
  const originalStatusOf = (record) => (
    (record.discrepancy_types && record.discrepancy_types.length > 0) ? 'discrepancy' : 'matched'
  );

  // Sync batch stats from an undo API response
  const syncBatchCounts = (counts) => {
    if (!counts) return;
    const patch = {
      matched_count: counts.matched_count,
      discrepancy_count: counts.discrepancy_count,
      resolved_count: counts.resolved_count
    };
    setSelectedBatch(prev => prev ? { ...prev, ...patch } : prev);
    setBatches(prev => prev.map(b => b.id === selectedBatch?.id ? { ...b, ...patch } : b));
  };

  // Undo a single resolved record, reverting to its original status
  const handleUndo = async (record) => {
    if (!selectedBatch) return;
    const revertTo = originalStatusOf(record);

    // OPTIMISTIC: revert locally
    setValidationRecords(prev => prev.map(r =>
      r.id === record.id
        ? { ...r, status: revertTo, resolution_source: null, resolution_notes: null }
        : r
    ));

    try {
      const response = await API.put(`/validation/${record.id}/undo`);
      syncBatchCounts(response.data);
      toast.success('Resolution undone');
    } catch (error) {
      // ROLLBACK
      setValidationRecords(prev => prev.map(r =>
        r.id === record.id ? { ...r, status: 'resolved' } : r
      ));
      toast.error(error.response?.data?.detail || 'Failed to undo');
    }
  };

  // Undo all resolved records for an employee
  const handleUndoAll = async (employeeId) => {
    if (!selectedBatch || undoingEmployee) return;

    const resolvedRecords = validationRecords.filter(
      r => r.employee_id === employeeId && r.status === 'resolved'
    );
    if (resolvedRecords.length === 0) {
      toast.info('No resolved entries to undo');
      return;
    }
    const resolvedIds = resolvedRecords.map(r => r.id);
    const prevSnapshot = resolvedRecords.map(r => ({ id: r.id, status: r.status, resolution_source: r.resolution_source }));

    // OPTIMISTIC: revert all locally
    setValidationRecords(prev => prev.map(r =>
      resolvedIds.includes(r.id)
        ? { ...r, status: originalStatusOf(r), resolution_source: null, resolution_notes: null }
        : r
    ));

    setUndoingEmployee(employeeId);
    try {
      const response = await API.post(`/batches/${selectedBatch.id}/undo-employee?employee_id=${encodeURIComponent(employeeId)}`);
      syncBatchCounts(response.data);
      toast.success(`Undone ${response.data.undone_count} entries`);
    } catch (error) {
      // ROLLBACK
      setValidationRecords(prev => prev.map(r => {
        const snap = prevSnapshot.find(s => s.id === r.id);
        return snap ? { ...r, status: snap.status, resolution_source: snap.resolution_source } : r;
      }));
      toast.error('Failed to undo entries');
    } finally {
      setUndoingEmployee(null);
    }
  };

  // ---- Validation allowances (per-day, flow into payroll) ----
  const isBatchApproved = () => selectedBatch?.approval_status === 'approved';

  const openAllowanceDialog = async (record, existing = null) => {
    setAllowanceRecord(record);
    setEditingAllowance(existing);
    setAllowanceName(existing?.name || '');
    setAllowanceAmount(existing?.amount ? String(existing.amount) : '');
    setAllowanceUnit(existing?.unit || 'per day');
    setAllowanceCode(existing?.code || 'MANUAL');
    setNoteSuggestions([]);
    setAllowanceDialogOpen(true);

    // Load available allowances (once)
    if (availableAllowances.length === 0) {
      try {
        const res = await API.get('/payroll/available-allowances');
        setAvailableAllowances(res.data || []);
      } catch (e) { /* non-blocking */ }
    }

    // Fetch AI suggestions parsed from this record's notes (only when adding new)
    if (!existing && (record.timesheet_notes || record.docket_notes || record.operator_notes)) {
      setLoadingSuggestions(true);
      try {
        const res = await API.get(`/validation/${record.id}/note-allowance-suggestions`);
        setNoteSuggestions(res.data?.suggestions || []);
      } catch (e) {
        setNoteSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }
  };

  const applyAllowanceSuggestion = (s) => {
    setAllowanceName(s.name || '');
    setAllowanceAmount(s.amount ? String(s.amount) : '');
    setAllowanceUnit(s.unit || 'per day');
    setAllowanceCode(s.code || 'MANUAL');
  };

  const applyAvailableAllowance = (code) => {
    const a = availableAllowances.find(x => x.code === code);
    if (a) {
      setAllowanceCode(a.code);
      setAllowanceName(a.name);
      setAllowanceUnit(a.unit || 'per day');
      if (a.amount) setAllowanceAmount(String(a.amount));
    }
  };

  const addAllowance = async () => {
    if (!allowanceRecord || !allowanceName.trim()) {
      toast.error('Please enter an allowance name');
      return;
    }
    const payload = {
      name: allowanceName.trim(),
      amount: parseFloat(allowanceAmount) || 0,
      unit: allowanceUnit,
      code: allowanceCode || 'MANUAL',
      source: 'Manual'
    };
    setAddingAllowance(true);
    try {
      if (editingAllowance) {
        const res = await API.put(`/validation/${allowanceRecord.id}/allowances/${editingAllowance.id}`, payload);
        const updated = res.data.allowance;
        setValidationRecords(prev => prev.map(r =>
          r.id === allowanceRecord.id
            ? { ...r, allowances: (r.allowances || []).map(a => a.id === editingAllowance.id ? updated : a) }
            : r
        ));
        toast.success('Allowance updated');
      } else {
        const res = await API.post(`/validation/${allowanceRecord.id}/allowances`, payload);
        const newAllowance = res.data.allowance;
        setValidationRecords(prev => prev.map(r =>
          r.id === allowanceRecord.id
            ? { ...r, allowances: [...(r.allowances || []), newAllowance] }
            : r
        ));
        toast.success('Allowance added');
      }
      setAllowanceDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save allowance');
    } finally {
      setAddingAllowance(false);
    }
  };

  const removeAllowance = async (record, allowanceId) => {
    // Optimistic
    setValidationRecords(prev => prev.map(r =>
      r.id === record.id
        ? { ...r, allowances: (r.allowances || []).filter(a => a.id !== allowanceId) }
        : r
    ));
    try {
      await API.delete(`/validation/${record.id}/allowances/${allowanceId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove allowance');
      fetchValidation(selectedBatch.id);
    }
  };

  const renderAllowanceStrip = (record) => {
    const allowances = record.allowances || [];
    const approved = isBatchApproved();
    return (
      <div className="flex flex-wrap gap-1.5 items-center" data-testid={`allowances-cell-${record.id}`}>
        <span className="text-xs font-medium text-slate-500 mr-1">Allowances:</span>
        {allowances.map((a) => (
          <Badge
            key={a.id}
            variant="outline"
            className="text-xs bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 gap-1 pr-1"
          >
            {approved ? (
              <span>{a.name}{a.amount ? ` $${Number(a.amount).toFixed(2)}` : ''}</span>
            ) : (
              <button
                onClick={() => openAllowanceDialog(record, a)}
                className="hover:underline"
                title="Edit allowance"
                data-testid={`edit-allowance-${a.id}`}
              >
                {a.name}{a.amount ? ` $${Number(a.amount).toFixed(2)}` : ''}
              </button>
            )}
            {!approved && (
              <button
                onClick={() => removeAllowance(record, a.id)}
                className="hover:text-red-600"
                data-testid={`remove-allowance-${a.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </Badge>
        ))}
        {allowances.length === 0 && approved && <span className="text-xs text-slate-400">None</span>}
        {!approved && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-1.5 text-xs text-fuchsia-700 hover:text-fuchsia-900 hover:bg-fuchsia-50"
            onClick={() => openAllowanceDialog(record)}
            data-testid={`add-allowance-btn-${record.id}`}
          >
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        )}
      </div>
    );
  };

  const renderJobFiles = (record, compact = false) => {
    const files = record.job_files || [];
    if (files.length === 0) return null;
    return (
      <Popover>
        <PopoverTrigger asChild>
          {compact ? (
            <button
              className="ml-1 text-slate-400 hover:text-slate-700 align-middle"
              onClick={(e) => e.stopPropagation()}
              title={`${files.length} file(s)`}
              data-testid={`job-files-icon-${record.id}`}
            >
              <Paperclip className="w-3.5 h-3.5 inline" />
            </button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="h-6 px-2 text-xs gap-1"
              onClick={(e) => e.stopPropagation()}
              data-testid={`job-files-btn-${record.id}`}
            >
              <Paperclip className="w-3 h-3" /> Files ({files.length})
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-2" onClick={(e) => e.stopPropagation()}>
          <p className="text-xs font-medium text-slate-500 px-1 pb-1">Job Files</p>
          <div className="space-y-1">
            {files.map((f, i) => (
              <a
                key={i}
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-md hover:bg-slate-100 text-slate-700"
                data-testid={`job-file-link-${record.id}-${i}`}
              >
                <FileText className="w-4 h-4 text-fuchsia-600 shrink-0" />
                <span className="truncate flex-1">{f.name}</span>
                <ExternalLink className="w-3 h-3 text-slate-400 shrink-0" />
              </a>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  const renderDayDetail = (record, colSpanCount) => {
    const hasTimes = record.ts_start_time || record.dk_start_time;
    const hasFiles = (record.job_files || []).length > 0;
    const approved = isBatchApproved();
    const hasAllowances = (record.allowances || []).length > 0;
    // While editable, show strip on every day; when approved, only if there's content
    if (approved && !hasTimes && !hasFiles && !hasAllowances) return null;
    return (
      <TableRow className="border-b-2 border-slate-200" key={`${record.id}-detail`}>
        <TableCell colSpan={colSpanCount} className="py-2 px-3 bg-slate-50/50">
          <div className="flex items-center justify-between gap-3 mb-2">
            {renderAllowanceStrip(record)}
            {hasFiles && renderJobFiles(record, false)}
          </div>
          {hasTimes && (
            <TimeGanttBar
              tsStart={record.ts_start_time}
              tsEnd={record.ts_end_time}
              dkStart={record.dk_start_time}
              dkEnd={record.dk_end_time}
              showLabels={false}
              showTimeLabels={true}
              height="h-8"
              breakMinutes={record.ts_break_minutes || record.dk_break_minutes || 0}
              ordinaryHours={8}
              ot1Threshold={10}
            />
          )}
        </TableCell>
      </TableRow>
    );
  };


  // ---- Bulk scan notes for allowances (per employee) ----
  const openScanDialog = async (employeeId, employeeName) => {
    setScanning(employeeId);
    setScanEmployeeName(employeeName || employeeId);
    try {
      const res = await API.get(`/batches/${selectedBatch.id}/employees/${encodeURIComponent(employeeId)}/allowance-scan`);
      const results = res.data?.results || [];
      if (results.length === 0) {
        toast.info('No allowances found in this employee\u2019s notes');
        return;
      }
      // Pre-check all suggestions
      const sel = {};
      results.forEach(r => r.suggestions.forEach((s, i) => { sel[`${r.record_id}::${i}`] = true; }));
      setScanResults(results);
      setScanSelections(sel);
      setScanDialogOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI scan failed');
    } finally {
      setScanning(null);
    }
  };

  const toggleScanSelection = (key) => {
    setScanSelections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const confirmScan = async () => {
    const items = [];
    scanResults.forEach(r => r.suggestions.forEach((s, i) => {
      if (scanSelections[`${r.record_id}::${i}`]) {
        items.push({ record_id: r.record_id, name: s.name, amount: s.amount, unit: s.unit, code: s.code });
      }
    }));
    if (items.length === 0) {
      toast.error('Select at least one allowance');
      return;
    }
    setApplyingScan(true);
    try {
      const res = await API.post(`/batches/${selectedBatch.id}/allowances/bulk`, { items });
      const addedMap = res.data?.added || {};
      // Merge into local records
      setValidationRecords(prev => prev.map(rec =>
        addedMap[rec.id]
          ? { ...rec, allowances: [...(rec.allowances || []), ...addedMap[rec.id]] }
          : rec
      ));
      toast.success(`Added ${res.data.added_count} allowances`);
      setScanDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add allowances');
    } finally {
      setApplyingScan(false);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    try {
      await API.delete(`/batches/${batchId}`);
      toast.success('Batch deleted');
      
      if (selectedBatch?.id === batchId) {
        setSelectedBatch(null);
        setValidationRecords([]);
      }
      fetchBatches();
    } catch (error) {
      toast.error('Failed to delete batch');
    }
  };

  // Helper functions
  const getStatusBadge = (status) => {
    switch (status) {
      case 'matched':
        return <Badge className="bg-green-100 text-green-700 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Match</Badge>;
      case 'discrepancy':
        return <Badge className="bg-amber-100 text-amber-700 text-xs"><AlertTriangle className="w-3 h-3 mr-1" />Discrepancy</Badge>;
      case 'resolved':
        return <Badge className="bg-blue-100 text-blue-700 text-xs"><Check className="w-3 h-3 mr-1" />Resolved</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{status}</Badge>;
    }
  };

  const getBatchStatusBadge = (batch) => {
    if (batch.approval_status === 'approved') {
      return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Approved</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700"><Clock3 className="w-3 h-3 mr-1" />Pending</Badge>;
  };

  const getDiscrepancyLabel = (type) => {
    switch (type) {
      case 'start_time_mismatch': return 'Start';
      case 'end_time_mismatch': return 'End';
      case 'break_mismatch': return 'Break';
      case 'missing_docket': return 'No Docket';
      case 'missing_timesheet': return 'No TS';
      default: return type;
    }
  };

  // Group validation records by employee
  const groupedRecords = React.useMemo(() => {
    if (!groupByEmployee) return null;
    
    const groups = {};
    validationRecords.forEach(record => {
      const empId = record.employee_id;
      if (!groups[empId]) {
        groups[empId] = {
          employee_id: empId,
          employee_name: record.employee_name,
          records: [],
          stats: { matched: 0, discrepancy: 0, resolved: 0, total: 0 }
        };
      }
      groups[empId].records.push(record);
      groups[empId].stats.total++;
      if (record.status === 'matched') groups[empId].stats.matched++;
      else if (record.status === 'discrepancy') groups[empId].stats.discrepancy++;
      else if (record.status === 'resolved') groups[empId].stats.resolved++;
    });
    
    // Sort groups by employee name
    return Object.values(groups).sort((a, b) => 
      (a.employee_name || a.employee_id).localeCompare(b.employee_name || b.employee_id)
    );
  }, [validationRecords, groupByEmployee]);

  // Toggle employee expansion
  const toggleEmployee = (empId) => {
    setExpandedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(empId)) {
        newSet.delete(empId);
      } else {
        newSet.add(empId);
      }
      return newSet;
    });
  };

  // Expand/collapse all employees
  const expandAllEmployees = () => {
    if (groupedRecords) {
      setExpandedEmployees(new Set(groupedRecords.map(g => g.employee_id)));
    }
  };

  const collapseAllEmployees = () => {
    setExpandedEmployees(new Set());
  };

  // Manual Import handlers
  const handleFetchManualData = async () => {
    if (!manualImportWeek) {
      toast.error('Please select a Week Commencing date');
      return;
    }
    
    setFetchingManualData(true);
    try {
      const response = await API.post('/settings/fetch-week-data', { 
        week_commencing: manualImportWeek 
      });
      
      if (response.data.preview) {
        setManualPreviewData(response.data);
        setShowManualImportDialog(false);
        setManualPreviewModalOpen(true);
      } else {
        toast.info(response.data.message || 'No data found for the selected week');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch week data');
    } finally {
      setFetchingManualData(false);
    }
  };

  const handleConfirmManualImport = async () => {
    if (!manualPreviewData) return;
    
    setConfirmingManualImport(true);
    try {
      const response = await API.post('/settings/confirm-week-import', {
        week_commencing: manualPreviewData.week_commencing,
        batch_name: manualPreviewData.batch_name,
        timesheet_entries: manualPreviewData.timesheet_entries,
        docket_entries: manualPreviewData.docket_entries,
        docket_items: manualPreviewData.docket_items || []
      });
      
      setManualImportSuccessData({
        batch_name: response.data.batch_name || manualPreviewData.batch_name,
        batch_id: response.data.batch_id,
        timesheet_count: response.data.timesheet_count || 0,
        docket_count: response.data.docket_count || 0,
        docket_items_count: response.data.docket_items_count || 0,
        validation_count: response.data.validation_count || 0,
        matched_count: response.data.matched_count || 0,
        discrepancy_count: response.data.discrepancy_count || 0
      });
      
      setManualPreviewModalOpen(false);
      setManualPreviewData(null);
      setManualImportWeek('');
      setManualImportSuccessModalOpen(true);
      
      // Refresh batches list
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm import');
    } finally {
      setConfirmingManualImport(false);
    }
  };

  const handleCancelManualPreview = () => {
    setManualPreviewModalOpen(false);
    setManualPreviewData(null);
    toast.info('Import cancelled');
  };

  const handleCloseManualSuccessModal = () => {
    setManualImportSuccessModalOpen(false);
    setManualImportSuccessData(null);
  };

  // Docket Items handlers
  const handleViewDocketItems = async (docketNumber) => {
    if (!docketNumber) return;
    
    setSelectedDocketNumber(docketNumber);
    setDocketItemsModalOpen(true);
    setLoadingDocketItems(true);
    
    try {
      const response = await API.get(`/docket-items/${docketNumber}`);
      setDocketItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch docket items');
      setDocketItems([]);
    } finally {
      setLoadingDocketItems(false);
    }
  };

  const handleCloseDocketItemsModal = () => {
    setDocketItemsModalOpen(false);
    setSelectedDocketNumber(null);
    setDocketItems([]);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-equipr-yellow">
              <Clock className="w-5 h-5 text-equipr-black" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Timesheets & Validation</h1>
          </div>
          <p className="text-slate-600 mt-1 ml-12">Import timesheets and dockets, validate time matches</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowManualImportDialog(true)} className="gap-2">
            <Database className="w-4 h-4" />
            Manual Import
          </Button>
          <Button onClick={() => setShowImportDialog(true)} className="gap-2">
            <Upload className="w-4 h-4" />
            New Import
          </Button>
        </div>
      </div>

      {/* Import Batches Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Import Batches</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6">
              <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : batches.length === 0 ? (
            <div className="text-center py-6">
              <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No import batches yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch Name</TableHead>
                  <TableHead>Imported</TableHead>
                  <TableHead>Records</TableHead>
                  <TableHead>Matched</TableHead>
                  <TableHead>Discrepancies</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Approved By</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((batch) => (
                  <TableRow 
                    key={batch.id} 
                    className={selectedBatch?.id === batch.id ? 'bg-indigo-50' : 'hover:bg-slate-50 cursor-pointer'}
                    onClick={() => setSelectedBatch(batch)}
                  >
                    <TableCell className="font-medium">{batch.name}</TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(batch.created_at)}
                    </TableCell>
                    <TableCell>{batch.timesheet_count + batch.docket_count}</TableCell>
                    <TableCell>
                      <span className="text-green-600 font-medium">{batch.matched_count}</span>
                    </TableCell>
                    <TableCell>
                      {batch.discrepancy_count > 0 ? (
                        <span className="text-amber-600 font-medium">{batch.discrepancy_count}</span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </TableCell>
                    <TableCell>{getBatchStatusBadge(batch)}</TableCell>
                    <TableCell className="text-sm">
                      {batch.approved_by ? (
                        <div>
                          <p className="font-medium text-slate-700">{batch.approved_by}</p>
                          {batch.approved_at && (
                            <p className="text-xs text-slate-400">
                              {new Date(batch.approved_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setSelectedBatch(batch)}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Validate
                        </Button>
                        {batch.approval_status !== 'approved' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-green-600 hover:text-green-700"
                            onClick={() => handleApproveBatch(batch.id)}
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Approve
                          </Button>
                        )}
                        {batch.approval_status === 'approved' && batch.status !== 'processed' && (
                          <Button 
                            size="sm" 
                            className="bg-purple-600 hover:bg-purple-700 text-white gap-1"
                            onClick={() => handleProcessPayroll(batch.id)}
                            disabled={processingPayroll === batch.id}
                          >
                            {processingPayroll === batch.id ? (
                              <>
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <Calculator className="w-3 h-3" />
                                Process Payroll
                              </>
                            )}
                          </Button>
                        )}
                        {batch.status === 'processed' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-purple-600 hover:text-purple-700"
                            onClick={() => navigate('/payroll')}
                          >
                            <ArrowRight className="w-3 h-3 mr-1" />
                            View Payroll
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Batch?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete &quot;{batch.name}&quot; and all associated data.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteBatch(batch.id)} className="bg-red-600 hover:bg-red-700">
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Validation Records */}
      {selectedBatch && (
        <Card className={fullScreenMode ? 'fixed inset-4 z-50 flex flex-col' : ''}>
          <CardHeader className="pb-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  {selectedBatch.name}
                  {getBatchStatusBadge(selectedBatch)}
                </CardTitle>
                <CardDescription>
                  {validationRecords.length} records • {selectedBatch.timesheet_count} timesheets, {selectedBatch.docket_count} dockets
                  {selectedBatch.approval_status === 'approved' && (
                    <Badge className="ml-2 bg-green-100 text-green-700">Approved - Read Only</Badge>
                  )}
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => fetchValidation(selectedBatch.id)}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
                {selectedBatch.approval_status !== 'approved' && selectedBatch.matched_count > 0 && selectedBatch.resolved_count < (selectedBatch.matched_count + selectedBatch.discrepancy_count) && (
                  <Button size="sm" variant="outline" onClick={handleResolveAllMatched}>
                    <Check className="w-4 h-4 mr-1" />
                    Auto-Resolve
                  </Button>
                )}
                {selectedBatch.approval_status !== 'approved' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleScanNoteAllowances}
                    disabled={scanningAllowances}
                    className="gap-1"
                    data-testid="scan-note-allowances-btn"
                  >
                    {scanningAllowances ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <DollarSign className="w-4 h-4" />
                    )}
                    Scan $ Allowances
                  </Button>
                )}
                {/* Group by Employee Toggle */}
                <Button 
                  variant={groupByEmployee ? "default" : "outline"} 
                  size="sm" 
                  onClick={() => setGroupByEmployee(!groupByEmployee)}
                  className="gap-1"
                >
                  <User className="w-4 h-4" />
                  {groupByEmployee ? 'Grouped' : 'Group'}
                </Button>
                {groupByEmployee && groupedRecords && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={expandAllEmployees} className="h-8 px-2 text-xs">
                      Expand All
                    </Button>
                    <Button variant="ghost" size="sm" onClick={collapseAllEmployees} className="h-8 px-2 text-xs">
                      Collapse
                    </Button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <Select value={validationFilter} onValueChange={setValidationFilter}>
                    <SelectTrigger className="w-32 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="matched">Matched</SelectItem>
                      <SelectItem value="discrepancy">Discrepancy</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <TimeGanttLegend />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFullScreenMode(!fullScreenMode)}
                >
                  {fullScreenMode ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className={fullScreenMode ? 'flex-1 overflow-auto' : ''}>
            {loadingValidation ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : validationRecords.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">No records found</p>
              </div>
            ) : groupByEmployee && groupedRecords ? (
              /* Grouped by Employee View */
              <div className="space-y-3">
                {groupedRecords.map((group) => (
                  <div key={group.employee_id} className="border rounded-lg overflow-hidden">
                    {/* Employee Header - Clickable */}
                    <div 
                      className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div 
                        className="flex items-center gap-3 flex-1 cursor-pointer"
                        onClick={() => toggleEmployee(group.employee_id)}
                      >
                        {expandedEmployees.has(group.employee_id) ? (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{group.employee_name || group.employee_id}</p>
                            {group.stats.total > 0 && group.stats.discrepancy === 0 && (
                              <Badge
                                data-testid={`employee-done-pill-${group.employee_id}`}
                                className="bg-green-600 hover:bg-green-600 text-white text-xs gap-1 px-2"
                              >
                                <CheckCircle2 className="w-3 h-3" />
                                Done
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">{group.employee_id}</p>
                          {employeeNotes[group.employee_id] && (
                            <p
                              data-testid={`employee-note-preview-${group.employee_id}`}
                              className="text-xs text-amber-700 mt-1 flex items-start gap-1 max-w-xl"
                            >
                              <StickyNote className="w-3 h-3 mt-0.5 shrink-0" />
                              <span className="line-clamp-2">{employeeNotes[group.employee_id]}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const t = validationTotals[group.employee_id];
                          if (!t) return null;
                          const Stat = ({ label, value, cls }) => (
                            <div className="flex flex-col items-end leading-tight">
                              <span className="text-[9px] uppercase tracking-wide text-slate-400">{label}</span>
                              <span className={`text-sm font-semibold tabular-nums ${cls}`}>{value}</span>
                            </div>
                          );
                          return (
                            <div
                              className="hidden md:flex items-center gap-4 mr-3 pr-4 border-r border-slate-200"
                              data-testid={`employee-totals-${group.employee_id}`}
                            >
                              <Stat label="Total" value={`${t.total_hours}h`} cls="text-slate-900" />
                              <Stat label="Ordinary" value={`${t.ordinary_hours}h`} cls="text-green-600" />
                              <Stat label="OT 1.5x" value={`${t.ot1_hours}h`} cls="text-amber-600" />
                              <Stat label="OT 2.0x" value={`${t.ot2_hours}h`} cls="text-red-500" />
                              <Stat label="Allowances" value={`$${(t.allowances_total || 0).toFixed(2)}`} cls="text-purple-600" />
                              <Stat
                                label="Est. Pay"
                                value={t.gross_pay != null ? `$${t.gross_pay.toFixed(2)}` : '—'}
                                cls="text-primary"
                              />
                            </div>
                          );
                        })()}
                        <Badge variant="outline" className="text-xs">{group.stats.total} days</Badge>
                        {group.stats.matched > 0 && (
                          <Badge className="bg-green-100 text-green-700 text-xs">{group.stats.matched} matched</Badge>
                        )}
                        {group.stats.discrepancy > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">{group.stats.discrepancy} issues</Badge>
                        )}
                        {group.stats.resolved > 0 && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">{group.stats.resolved} resolved</Badge>
                        )}

                        {/* Undo All - revert every resolved record for this employee */}
                        {group.stats.resolved > 0 && selectedBatch?.approval_status !== 'approved' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 gap-1"
                            disabled={undoingEmployee === group.employee_id}
                            onClick={(e) => { e.stopPropagation(); handleUndoAll(group.employee_id); }}
                            data-testid={`undo-all-btn-${group.employee_id}`}
                          >
                            {undoingEmployee === group.employee_id ? (
                              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <RotateCcw className="w-3.5 h-3.5" />
                            )}
                            <span className="text-xs">Undo All</span>
                          </Button>
                        )}

                        {/* Employee Note - add/edit a per-week note against this employee */}
                        <Popover
                          open={notePopoverOpen === group.employee_id}
                          onOpenChange={(open) => {
                            if (open) {
                              setNoteDraft(employeeNotes[group.employee_id] || '');
                              setNotePopoverOpen(group.employee_id);
                            } else {
                              setNotePopoverOpen(null);
                            }
                          }}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className={`h-7 px-2 gap-1 ${employeeNotes[group.employee_id] ? 'border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100' : ''}`}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`employee-note-btn-${group.employee_id}`}
                            >
                              <StickyNote className="w-3.5 h-3.5" />
                              <span className="text-xs">{employeeNotes[group.employee_id] ? 'Edit Note' : 'Note'}</span>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="w-80" onClick={(e) => e.stopPropagation()}>
                            <div className="space-y-3">
                              <div>
                                <p className="text-sm font-medium">Note for {group.employee_name || group.employee_id}</p>
                                <p className="text-xs text-slate-500">Visible for this week only</p>
                              </div>
                              <Textarea
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                placeholder="Add a note about this employee's times…"
                                rows={4}
                                className="text-sm"
                                data-testid={`employee-note-textarea-${group.employee_id}`}
                              />
                              <div className="flex justify-end gap-2">
                                <Button variant="ghost" size="sm" onClick={() => setNotePopoverOpen(null)}>Cancel</Button>
                                <Button
                                  size="sm"
                                  onClick={() => saveEmployeeNote(group.employee_id)}
                                  disabled={savingNote}
                                  data-testid={`employee-note-save-${group.employee_id}`}
                                >
                                  {savingNote ? 'Saving…' : 'Save Note'}
                                </Button>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>

                        {/* Scan notes for allowances (AI) */}
                        {selectedBatch?.approval_status !== 'approved' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 gap-1 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
                            disabled={scanning === group.employee_id}
                            onClick={(e) => { e.stopPropagation(); openScanDialog(group.employee_id, group.employee_name); }}
                            data-testid={`scan-allowances-btn-${group.employee_id}`}
                          >
                            {scanning === group.employee_id ? (
                              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Sparkles className="w-3.5 h-3.5" />
                            )}
                            <span className="text-xs">Scan Notes</span>
                          </Button>
                        )}

                        {/* Quick Resolve Dropdown - Only show if there are discrepancies and batch not approved */}
                        {group.stats.discrepancy > 0 && selectedBatch?.approval_status !== 'approved' && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-7 px-2 gap-1 ml-2"
                                disabled={bulkResolving === group.employee_id}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {bulkResolving === group.employee_id ? (
                                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Zap className="w-3.5 h-3.5" />
                                )}
                                <span className="text-xs">Quick Resolve</span>
                                <ChevronDown className="w-3 h-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuItem 
                                onClick={() => handleBulkResolve(group.employee_id, 'timesheet')}
                                className="gap-2 cursor-pointer"
                              >
                                <Clock className="w-4 h-4 text-blue-500" />
                                <div>
                                  <p className="font-medium">Accept All Timesheet Times</p>
                                  <p className="text-xs text-slate-500">Use timesheet start/end for {group.stats.discrepancy} entries</p>
                                </div>
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleBulkResolve(group.employee_id, 'docket')}
                                className="gap-2 cursor-pointer"
                              >
                                <ClipboardList className="w-4 h-4 text-green-500" />
                                <div>
                                  <p className="font-medium">Accept All Docket Times</p>
                                  <p className="text-xs text-slate-500">Use docket start/end for {group.stats.discrepancy} entries</p>
                                </div>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                    
                    {/* Employee Records - Collapsible */}
                    {expandedEmployees.has(group.employee_id) && (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50/50">
                              <TableHead className="whitespace-nowrap min-w-[100px]">Date</TableHead>
                              <TableHead className="whitespace-nowrap">Site #</TableHead>
                              <TableHead className="whitespace-nowrap">Docket #</TableHead>
                              <TableHead className="whitespace-nowrap">Role</TableHead>
                              <TableHead className="whitespace-nowrap">Equipment</TableHead>
                              <TableHead className="whitespace-nowrap">Timesheet</TableHead>
                              <TableHead className="whitespace-nowrap">Docket</TableHead>
                              <TableHead className="whitespace-nowrap min-w-[150px]">Notes</TableHead>
                              <TableHead className="whitespace-nowrap">Status</TableHead>
                              <TableHead className="whitespace-nowrap">Issues</TableHead>
                              <TableHead className="text-right whitespace-nowrap">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.records.sort((a, b) => a.date.localeCompare(b.date)).map((record) => (
                              <React.Fragment key={record.id}>
                                <TableRow className={record.status === 'discrepancy' ? 'bg-amber-50/50' : ''}>
                                  <TableCell className="font-mono text-xs whitespace-nowrap min-w-[100px]">{formatDate(record.date)}</TableCell>
                                  <TableCell className="font-mono text-xs whitespace-nowrap">
                                    {record.site_id || '—'}
                                  </TableCell>
                                  <TableCell className="font-mono text-xs whitespace-nowrap">
                                    {(record.dk_docket_number || record.docket_number) ? (
                                      <button
                                        onClick={() => handleViewDocketItems(record.dk_docket_number || record.docket_number)}
                                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                      >
                                        {record.dk_docket_number || record.docket_number}
                                      </button>
                                    ) : '—'}
                                    {renderJobFiles(record, true)}
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap">
                                    {record.role ? (
                                      <Badge variant="outline" className="text-xs">{record.role}</Badge>
                                    ) : '—'}
                                  </TableCell>
                                  <TableCell className="text-xs whitespace-nowrap max-w-[120px] truncate" title={record.equipment_type}>
                                    {record.equipment_type || '—'}
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap">
                                    {record.ts_start_time ? (
                                      <div className="text-xs">
                                        <span className="font-mono">{record.ts_start_time}→{record.ts_end_time}</span>
                                        <span className="text-slate-400 ml-1">({record.ts_break_minutes || 0}m)</span>
                                      </div>
                                    ) : '—'}
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap">
                                    {record.dk_start_time ? (
                                      <div className="text-xs">
                                        <span className="font-mono">{record.dk_start_time}→{record.dk_end_time}</span>
                                        <span className="text-slate-400 ml-1">({record.dk_break_minutes || 0}m)</span>
                                      </div>
                                    ) : '—'}
                                  </TableCell>
                                  <TableCell className="text-xs max-w-[200px]">
                                    <div className="space-y-1">
                                      {record.timesheet_notes && (
                                        <span className="text-slate-600 line-clamp-2 block" title={record.timesheet_notes}>
                                          {record.timesheet_notes}
                                        </span>
                                      )}
                                      {record.docket_notes && (
                                        <span className="text-blue-600 line-clamp-2 block" title={`DK: ${record.docket_notes}`}>
                                          <span className="font-medium">DK:</span> {record.docket_notes}
                                        </span>
                                      )}
                                      {record.operator_notes && (
                                        <span className="text-amber-600 line-clamp-2 block" title={`OP: ${record.operator_notes}`}>
                                          <span className="font-medium">OP:</span> {record.operator_notes}
                                        </span>
                                      )}
                                      {!record.timesheet_notes && !record.docket_notes && !record.operator_notes && '—'}
                                    </div>
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap">{getStatusBadge(record.status)}</TableCell>
                                  <TableCell className="whitespace-nowrap">
                                    <div className="flex flex-wrap gap-0.5">
                                      {record.discrepancy_types?.map((type, i) => (
                                        <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200 px-1">
                                          {getDiscrepancyLabel(type)}
                                        </Badge>
                                      ))}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right whitespace-nowrap">
                                    {record.status !== 'resolved' ? (
                                      selectedBatch?.approval_status === 'approved' ? (
                                        <span className="text-xs text-slate-400">Locked</span>
                                      ) : (
                                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openResolveDialog(record)}>
                                          Resolve
                                        </Button>
                                      )
                                    ) : (
                                      selectedBatch?.approval_status === 'approved' ? (
                                        <span className="text-xs text-slate-500">{record.resolution_source}</span>
                                      ) : (
                                        <div className="flex items-center justify-end gap-2">
                                          <span className="text-xs text-slate-500">{record.resolution_source}</span>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-900"
                                            onClick={() => handleUndo(record)}
                                            data-testid={`undo-btn-${record.id}`}
                                          >
                                            <RotateCcw className="w-3.5 h-3.5 mr-1" />
                                            Undo
                                          </Button>
                                        </div>
                                      )
                                    )}
                                  </TableCell>
                                </TableRow>
                                {/* Per-day detail: allowances + files + Gantt */}
                                {renderDayDetail(record, 11)}
                              </React.Fragment>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              /* Flat List View */
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Employee</TableHead>
                      <TableHead className="whitespace-nowrap min-w-[100px]">Date</TableHead>
                      <TableHead className="whitespace-nowrap">Site #</TableHead>
                      <TableHead className="whitespace-nowrap">Docket #</TableHead>
                      <TableHead className="whitespace-nowrap">Role</TableHead>
                      <TableHead className="whitespace-nowrap">Equipment</TableHead>
                      <TableHead className="whitespace-nowrap">Timesheet</TableHead>
                      <TableHead className="whitespace-nowrap">Docket</TableHead>
                      <TableHead className="whitespace-nowrap min-w-[150px]">Notes</TableHead>
                      <TableHead className="whitespace-nowrap">Status</TableHead>
                      <TableHead className="whitespace-nowrap">Issues</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {validationRecords.map((record) => (
                      <React.Fragment key={record.id}>
                        <TableRow className={record.status === 'discrepancy' ? 'bg-amber-50/50' : ''}>
                          <TableCell className="whitespace-nowrap">
                            <div>
                              <p className="font-medium text-sm">{record.employee_name || record.employee_id}</p>
                              {record.employee_name && (
                                <p className="text-xs text-slate-500">{record.employee_id}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-xs whitespace-nowrap min-w-[100px]">{formatDate(record.date)}</TableCell>
                          <TableCell className="font-mono text-xs whitespace-nowrap">
                            {record.site_id || '—'}
                          </TableCell>
                          <TableCell className="font-mono text-xs whitespace-nowrap">
                            {(record.dk_docket_number || record.docket_number) ? (
                              <button
                                onClick={() => handleViewDocketItems(record.dk_docket_number || record.docket_number)}
                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                              >
                                {record.dk_docket_number || record.docket_number}
                              </button>
                            ) : '—'}
                            {renderJobFiles(record, true)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {record.role ? (
                              <Badge variant="outline" className="text-xs">{record.role}</Badge>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap max-w-[120px] truncate" title={record.equipment_type}>
                            {record.equipment_type || '—'}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {record.ts_start_time ? (
                              <div className="text-xs">
                                <span className="font-mono">{record.ts_start_time}→{record.ts_end_time}</span>
                                <span className="text-slate-400 ml-1">({record.ts_break_minutes || 0}m)</span>
                              </div>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {record.dk_start_time ? (
                              <div className="text-xs">
                                <span className="font-mono">{record.dk_start_time}→{record.dk_end_time}</span>
                                <span className="text-slate-400 ml-1">({record.dk_break_minutes || 0}m)</span>
                              </div>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="text-xs max-w-[200px]">
                            <div className="space-y-1">
                              {record.timesheet_notes && (
                                <span className="text-slate-600 line-clamp-2 block" title={record.timesheet_notes}>
                                  {record.timesheet_notes}
                                </span>
                              )}
                              {record.docket_notes && (
                                <span className="text-blue-600 line-clamp-2 block" title={`DK: ${record.docket_notes}`}>
                                  <span className="font-medium">DK:</span> {record.docket_notes}
                                </span>
                              )}
                              {record.operator_notes && (
                                <span className="text-amber-600 line-clamp-2 block" title={`OP: ${record.operator_notes}`}>
                                  <span className="font-medium">OP:</span> {record.operator_notes}
                                </span>
                              )}
                              {!record.timesheet_notes && !record.docket_notes && !record.operator_notes && '—'}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{getStatusBadge(record.status)}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            <div className="flex flex-wrap gap-0.5">
                              {record.discrepancy_types?.map((type, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200 px-1">
                                  {getDiscrepancyLabel(type)}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            {record.status !== 'resolved' ? (
                              selectedBatch?.approval_status === 'approved' ? (
                                <span className="text-xs text-slate-400">Locked</span>
                              ) : (
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openResolveDialog(record)}>
                                  Resolve
                                </Button>
                              )
                            ) : (
                              selectedBatch?.approval_status === 'approved' ? (
                                <span className="text-xs text-slate-500">{record.resolution_source}</span>
                              ) : (
                                <div className="flex items-center justify-end gap-2">
                                  <span className="text-xs text-slate-500">{record.resolution_source}</span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-xs text-slate-500 hover:text-slate-900"
                                    onClick={() => handleUndo(record)}
                                    data-testid={`undo-btn-${record.id}`}
                                  >
                                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                                    Undo
                                  </Button>
                                </div>
                              )
                            )}
                          </TableCell>
                        </TableRow>
                        {/* Per-day detail: allowances + files + Gantt */}
                        {renderDayDetail(record, 12)}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Full screen overlay */}
      {fullScreenMode && (
        <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setFullScreenMode(false)} />
      )}

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Import Batch</DialogTitle>
            <DialogDescription>
              Upload an Excel file with tabs or separate CSV files
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Batch Name *</Label>
              <Input
                placeholder="e.g., Week Ending 2025-01-05"
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant={importMode === 'xlsx' ? 'default' : 'outline'}
                onClick={() => { setImportMode('xlsx'); setTimesheetFile(null); setDocketFile(null); }}
                className="flex-1"
                size="sm"
              >
                <FileText className="w-4 h-4 mr-2" />
                Excel (.xlsx)
              </Button>
              <Button
                type="button"
                variant={importMode === 'csv' ? 'default' : 'outline'}
                onClick={() => { setImportMode('csv'); setExcelFile(null); }}
                className="flex-1"
                size="sm"
              >
                <ClipboardList className="w-4 h-4 mr-2" />
                CSV (2 files)
              </Button>
            </div>

            {importMode === 'xlsx' ? (
              <div
                {...getExcelRootProps()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
                  isExcelDragActive ? 'border-indigo-500 bg-indigo-50' :
                  excelFile ? 'border-green-500 bg-green-50' :
                  'border-slate-300 hover:border-slate-400'
                }`}
              >
                <input {...getExcelInputProps()} />
                {excelFile ? (
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">{excelFile.name}</span>
                  </div>
                ) : (
                  <div>
                    <FileUp className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Drop Excel file here</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Requires tabs: &quot;Timesheet Data&quot; and &quot;Docket Data&quot;
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div
                  {...getTsRootProps()}
                  className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
                    isTsDragActive ? 'border-indigo-500 bg-indigo-50' :
                    timesheetFile ? 'border-green-500 bg-green-50' :
                    'border-slate-300 hover:border-slate-400'
                  }`}
                >
                  <input {...getTsInputProps()} />
                  {timesheetFile ? (
                    <div className="flex items-center justify-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">{timesheetFile.name}</span>
                    </div>
                  ) : (
                    <div>
                      <FileUp className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                      <p className="text-sm text-slate-600">Timesheet CSV</p>
                    </div>
                  )}
                </div>
                <div
                  {...getDkRootProps()}
                  className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
                    isDkDragActive ? 'border-indigo-500 bg-indigo-50' :
                    docketFile ? 'border-green-500 bg-green-50' :
                    'border-slate-300 hover:border-slate-400'
                  }`}
                >
                  <input {...getDkInputProps()} />
                  {docketFile ? (
                    <div className="flex items-center justify-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">{docketFile.name}</span>
                    </div>
                  ) : (
                    <div>
                      <FileUp className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                      <p className="text-sm text-slate-600">Docket CSV</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={importing || !batchName || (importMode === 'xlsx' ? !excelFile : (!timesheetFile || !docketFile))}
            >
              {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolution Dialog */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Resolve Discrepancy</DialogTitle>
            <DialogDescription>
              {selectedRecord?.employee_name} - {formatDate(selectedRecord?.date)}
            </DialogDescription>
          </DialogHeader>

          {selectedRecord && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-xs text-slate-500">Timesheet</Label>
                  {selectedRecord.ts_start_time ? (
                    <div className="mt-1">
                      <p className="font-mono">{selectedRecord.ts_start_time} → {selectedRecord.ts_end_time}</p>
                      <p className="text-sm text-slate-500">{selectedRecord.ts_break_minutes || 0}m break</p>
                    </div>
                  ) : (
                    <p className="text-slate-400 mt-1">No timesheet</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Docket</Label>
                  {selectedRecord.dk_start_time ? (
                    <div className="mt-1">
                      <p className="font-mono">{selectedRecord.dk_start_time} → {selectedRecord.dk_end_time}</p>
                      <p className="text-sm text-slate-500">{selectedRecord.dk_break_minutes || 0}m break</p>
                    </div>
                  ) : (
                    <p className="text-slate-400 mt-1">No docket</p>
                  )}
                </div>
              </div>
              
              {/* AI Suggestion Panel */}
              <div className={`p-3 rounded-lg border ${
                aiSuggestion?.has_suggestion 
                  ? 'bg-purple-50 border-purple-200' 
                  : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-start gap-2">
                  {loadingAiSuggestion ? (
                    <>
                      <RefreshCw className="w-4 h-4 text-purple-500 animate-spin mt-0.5" />
                      <span className="text-sm text-slate-600">Analyzing patterns...</span>
                    </>
                  ) : aiSuggestion?.has_suggestion ? (
                    <>
                      <Sparkles className="w-4 h-4 text-purple-500 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-purple-700">
                            AI suggests: <span className="capitalize">{aiSuggestion.suggested_source}</span>
                          </span>
                          <Badge className={`text-xs ${
                            aiSuggestion.confidence_label === 'High' ? 'bg-green-100 text-green-700' :
                            aiSuggestion.confidence_label === 'Medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {aiSuggestion.confidence}% confidence
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">{aiSuggestion.reason}</p>
                        {aiSuggestion.match_reasons?.length > 0 && (
                          <ul className="text-xs text-slate-500 mt-1 list-disc list-inside">
                            {aiSuggestion.match_reasons.map((r, i) => <li key={i}>{r}</li>)}
                          </ul>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-2 h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-100"
                          onClick={applyAiSuggestion}
                        >
                          <Sparkles className="w-3 h-3 mr-1" />
                          Apply Suggestion
                        </Button>
                      </div>
                    </>
                  ) : aiSuggestion?.ai_disabled ? (
                    <>
                      <Brain className="w-4 h-4 text-slate-300 mt-0.5" />
                      <div className="flex-1">
                        <span className="text-sm text-slate-500">AI suggestions disabled</span>
                        <p className="text-xs text-slate-400 mt-1">
                          Enable in Settings → AI Learning. ({aiSuggestion.patterns_analyzed || 0} patterns learned)
                        </p>
                      </div>
                    </>
                  ) : aiSuggestion ? (
                    <>
                      <Brain className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div className="flex-1">
                        <span className="text-sm text-slate-600">{aiSuggestion.message}</span>
                        {aiSuggestion.breakdown && Object.keys(aiSuggestion.breakdown).length > 0 && (
                          <div className="flex gap-2 mt-1">
                            {Object.entries(aiSuggestion.breakdown).map(([source, pct]) => (
                              <Badge key={source} variant="outline" className="text-xs capitalize">
                                {source}: {pct}%
                              </Badge>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          {aiSuggestion.patterns_analyzed || 0} patterns analyzed
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Brain className="w-4 h-4 text-slate-400 mt-0.5" />
                      <span className="text-sm text-slate-500">AI learning in progress...</span>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Use Times From</Label>
                <Select value={resolutionSource} onValueChange={setResolutionSource}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="timesheet" disabled={!selectedRecord.ts_start_time}>Timesheet</SelectItem>
                    <SelectItem value="docket" disabled={!selectedRecord.dk_start_time}>Docket</SelectItem>
                    <SelectItem value="manual">Manual Override</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {resolutionSource === 'manual' && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Start</Label>
                    <Input type="time" value={manualStart} onChange={(e) => setManualStart(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>End</Label>
                    <Input type="time" value={manualEnd} onChange={(e) => setManualEnd(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Break</Label>
                    <Input type="number" value={manualBreak} onChange={(e) => setManualBreak(e.target.value)} />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Resolution notes..."
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleResolve} disabled={resolving}>
              {resolving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Import - Week Selection Dialog */}
      <Dialog open={showManualImportDialog} onOpenChange={setShowManualImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-indigo-600" />
              Manual Import from Database
            </DialogTitle>
            <DialogDescription>
              Fetch timesheet and docket data from EQUIPR Allocate for a specific week
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="manual-week">Week Commencing (Monday)</Label>
              <Input
                id="manual-week"
                type="date"
                value={manualImportWeek}
                onChange={(e) => setManualImportWeek(e.target.value)}
                className="w-full"
              />
              <p className="text-xs text-slate-500">
                Select the Monday of the week you want to import
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualImportDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleFetchManualData} disabled={fetchingManualData || !manualImportWeek} className="gap-2">
              {fetchingManualData ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Fetching...
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  Preview Data
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Import - Preview Modal */}
      <Dialog open={manualPreviewModalOpen} onOpenChange={(open) => !open && handleCancelManualPreview()}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Preview Import Data
            </DialogTitle>
            <DialogDescription>
              {manualPreviewData && (
                <span>
                  <strong>{manualPreviewData.batch_name}</strong> — Review the data below before importing
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {manualPreviewData && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{manualPreviewData.summary?.timesheet_count || 0}</p>
                  <p className="text-xs text-blue-600">Timesheets</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{manualPreviewData.summary?.docket_count || 0}</p>
                  <p className="text-xs text-amber-600">Dockets</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{manualPreviewData.summary?.employee_count || 0}</p>
                  <p className="text-xs text-green-600">Employees</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-sm font-medium text-slate-700">
                    {manualPreviewData.summary?.date_range?.start || '—'}
                  </p>
                  <p className="text-xs text-slate-500">to {manualPreviewData.summary?.date_range?.end || '—'}</p>
                </div>
              </div>
              
              {/* Tabs for Timesheet and Docket Data */}
              <Tabs defaultValue="timesheets" className="flex-1 flex flex-col overflow-hidden">
                <TabsList className="mb-2">
                  <TabsTrigger value="timesheets" className="gap-2">
                    <FileSpreadsheet className="w-4 h-4" />
                    Timesheet Data ({manualPreviewData.timesheet_entries?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="dockets" className="gap-2">
                    <ClipboardList className="w-4 h-4" />
                    Docket Data ({manualPreviewData.docket_entries?.length || 0})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="timesheets" className="flex-1 overflow-auto border rounded-lg">
                  {manualPreviewData.timesheet_entries?.length > 0 ? (
                    <Table>
                      <TableHeader className="sticky top-0 bg-white z-10">
                        <TableRow>
                          <TableHead className="min-w-[80px]">Emp ID</TableHead>
                          <TableHead className="min-w-[120px]">Employee</TableHead>
                          <TableHead className="min-w-[90px]">Date</TableHead>
                          <TableHead>Start</TableHead>
                          <TableHead>Finish</TableHead>
                          <TableHead>Break</TableHead>
                          <TableHead className="min-w-[200px]">Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {manualPreviewData.timesheet_entries.map((entry, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-xs">{entry.employee_id}</TableCell>
                            <TableCell className="text-sm">{entry.employee_name}</TableCell>
                            <TableCell className="text-xs">{entry.date}</TableCell>
                            <TableCell className="text-xs">{entry.start_time}</TableCell>
                            <TableCell className="text-xs">{entry.end_time}</TableCell>
                            <TableCell className="text-xs">{entry.break_minutes || 0}m</TableCell>
                            <TableCell className="text-xs text-slate-600 max-w-[250px]" title={entry.timesheet_notes}>
                              {entry.timesheet_notes || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="flex items-center justify-center h-40 text-slate-500">
                      <div className="text-center">
                        <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p>No timesheet entries found</p>
                      </div>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="dockets" className="flex-1 overflow-auto border rounded-lg">
                  {manualPreviewData.docket_entries?.length > 0 ? (
                    <Table>
                      <TableHeader className="sticky top-0 bg-white z-10">
                        <TableRow>
                          <TableHead>Docket #</TableHead>
                          <TableHead className="min-w-[80px]">Emp ID</TableHead>
                          <TableHead className="min-w-[120px]">Employee</TableHead>
                          <TableHead className="min-w-[90px]">Date</TableHead>
                          <TableHead>Start</TableHead>
                          <TableHead>End</TableHead>
                          <TableHead>Break</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Equipment</TableHead>
                          <TableHead>Job #</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead className="min-w-[150px]">Docket Notes</TableHead>
                          <TableHead className="min-w-[150px]">Operator Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {manualPreviewData.docket_entries.map((entry, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-xs">{entry.docket_number || '—'}</TableCell>
                            <TableCell className="font-mono text-xs">{entry.employee_id}</TableCell>
                            <TableCell className="text-sm">{entry.employee_name}</TableCell>
                            <TableCell className="text-xs">{entry.date}</TableCell>
                            <TableCell className="text-xs">{entry.start_time}</TableCell>
                            <TableCell className="text-xs">{entry.end_time}</TableCell>
                            <TableCell className="text-xs">{entry.break_minutes || 0}m</TableCell>
                            <TableCell>
                              {entry.role ? <Badge variant="outline" className="text-xs">{entry.role}</Badge> : '—'}
                            </TableCell>
                            <TableCell className="text-xs max-w-[100px] truncate" title={entry.equipment_type}>{entry.equipment_type || '—'}</TableCell>
                            <TableCell className="font-mono text-xs">{entry.site_id || '—'}</TableCell>
                            <TableCell className="text-xs max-w-[150px] truncate" title={entry.site_name}>{entry.site_name || '—'}</TableCell>
                            <TableCell className="text-xs text-blue-600 max-w-[200px] truncate" title={entry.docket_notes}>{entry.docket_notes || '—'}</TableCell>
                            <TableCell className="text-xs text-amber-600 max-w-[200px] truncate" title={entry.operator_notes}>{entry.operator_notes || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="flex items-center justify-center h-40 text-slate-500">
                      <div className="text-center">
                        <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p>No docket entries found</p>
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
          
          <DialogFooter className="mt-4 pt-4 border-t">
            <Button variant="outline" onClick={handleCancelManualPreview} disabled={confirmingManualImport}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmManualImport} 
              disabled={confirmingManualImport || (!manualPreviewData?.timesheet_entries?.length && !manualPreviewData?.docket_entries?.length)}
              className="gap-2"
            >
              {confirmingManualImport ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Confirm Import
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Import - Success Modal */}
      <Dialog open={manualImportSuccessModalOpen} onOpenChange={handleCloseManualSuccessModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl">Import Successful!</DialogTitle>
            <DialogDescription className="text-center">
              {manualImportSuccessData?.batch_name}
            </DialogDescription>
          </DialogHeader>
          
          {manualImportSuccessData && (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{manualImportSuccessData.timesheet_count}</p>
                  <p className="text-xs text-blue-600">Timesheets</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{manualImportSuccessData.docket_count}</p>
                  <p className="text-xs text-amber-600">Dockets</p>
                </div>
              </div>
              
              {/* Validation Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-700 mb-2">Validation Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total Validation Records:</span>
                    <span className="font-medium">{manualImportSuccessData.validation_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-600">Matched:</span>
                    <span className="font-medium text-green-700">{manualImportSuccessData.matched_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-600">Discrepancies:</span>
                    <span className="font-medium text-amber-700">{manualImportSuccessData.discrepancy_count}</span>
                  </div>
                </div>
              </div>
              
              {/* Next Steps */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Next Step
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  The imported batch is now available below. Select it to review and verify entries, 
                  resolve any discrepancies, and approve for payroll processing.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button onClick={handleCloseManualSuccessModal} className="w-full gap-2">
              <CheckCircle className="w-4 h-4" />
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Docket Items Modal */}
      <Dialog open={docketItemsModalOpen} onOpenChange={handleCloseDocketItemsModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-amber-600" />
              Docket Line Items
            </DialogTitle>
            <DialogDescription>
              Docket #{selectedDocketNumber}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {loadingDocketItems ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : docketItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <ClipboardList className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-sm">No line items found for this docket</p>
                <p className="text-xs text-slate-400 mt-1">Line items may not have been imported yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead className="min-w-[300px]">Description</TableHead>
                    <TableHead className="text-right">Unit</TableHead>
                    <TableHead>Account ID</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {docketItems.map((item, idx) => (
                    <TableRow key={idx} className={item.account_id === 'INVOICEINFO' ? 'bg-slate-50' : ''}>
                      <TableCell className={item.account_id === 'INVOICEINFO' ? 'font-semibold text-slate-700' : ''}>
                        {item.description || '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {item.unit ? item.unit.toFixed(2) : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs font-mono">
                          {item.account_id || '—'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-medium">
                        {item.price ? `$${item.price.toFixed(2)}` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          
          {docketItems.length > 0 && (
            <div className="border-t pt-3 mt-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{docketItems.length} line item(s)</span>
                <span className="font-medium">
                  Total: ${docketItems.reduce((sum, item) => sum + (item.price || 0), 0).toFixed(2)}
                </span>
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button onClick={handleCloseDocketItemsModal}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Allowance Dialog (validation step) */}
      <Dialog open={allowanceDialogOpen} onOpenChange={setAllowanceDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="allowance-dialog">
          <DialogHeader>
            <DialogTitle>{editingAllowance ? 'Edit Allowance' : 'Add Allowance'}</DialogTitle>
            <DialogDescription>
              {allowanceRecord ? `${allowanceRecord.employee_name || allowanceRecord.employee_id} • ${formatDate(allowanceRecord.date)}` : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* AI suggestions from notes */}
            {(loadingSuggestions || noteSuggestions.length > 0) && (
              <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50/60 p-3">
                <p className="text-xs font-medium text-fuchsia-700 flex items-center gap-1 mb-2">
                  <Sparkles className="w-3.5 h-3.5" /> Suggested from notes
                </p>
                {loadingSuggestions ? (
                  <p className="text-xs text-slate-500 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Scanning notes…</p>
                ) : (
                  <div className="space-y-2">
                    {noteSuggestions.map((s, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 bg-white rounded-md border px-2 py-1.5">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{s.name}{s.amount ? ` — $${Number(s.amount).toFixed(2)}` : ''}</p>
                          {s.evidence && <p className="text-xs text-slate-500 truncate italic">"{s.evidence}"</p>}
                        </div>
                        <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" onClick={() => applyAllowanceSuggestion(s)} data-testid={`apply-suggestion-${i}`}>
                          Use
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pick from configured allowances */}
            {availableAllowances.length > 0 && (
              <div>
                <Label className="text-xs">Pick a configured allowance</Label>
                <Select value={allowanceCode} onValueChange={applyAvailableAllowance}>
                  <SelectTrigger className="mt-1" data-testid="allowance-select">
                    <SelectValue placeholder="Select an allowance" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAllowances.map((a, i) => (
                      <SelectItem key={i} value={a.code}>
                        {a.name}{a.amount ? ` ($${Number(a.amount).toFixed(2)})` : ''} · {a.source}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Allowance name</Label>
                <Input value={allowanceName} onChange={(e) => setAllowanceName(e.target.value)} placeholder="e.g. Site Allowance" className="mt-1" data-testid="allowance-name-input" />
              </div>
              <div>
                <Label className="text-xs">Amount ($)</Label>
                <Input type="number" step="0.01" value={allowanceAmount} onChange={(e) => setAllowanceAmount(e.target.value)} placeholder="0.00" className="mt-1" data-testid="allowance-amount-input" />
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setAllowanceDialogOpen(false)}>Cancel</Button>
            <Button onClick={addAllowance} disabled={addingAllowance} data-testid="allowance-add-confirm">
              {addingAllowance ? 'Saving…' : (editingAllowance ? 'Save Changes' : 'Add Allowance')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan Notes Review Dialog */}
      <Dialog open={scanDialogOpen} onOpenChange={setScanDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="scan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-fuchsia-600" /> Suggested Allowances — {scanEmployeeName}
            </DialogTitle>
            <DialogDescription>
              Found in the employee's notes. Untick anything you don't want, then add the rest.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[55vh] overflow-y-auto space-y-3 pr-1">
            {scanResults.map((r) => (
              <div key={r.record_id} className="rounded-lg border p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">{formatDate(r.date)}{r.docket_number ? ` · Docket ${r.docket_number}` : ''}</p>
                </div>
                <p className="text-xs text-slate-500 italic mb-2 line-clamp-2">{r.notes}</p>
                <div className="space-y-1.5">
                  {r.suggestions.map((s, i) => {
                    const key = `${r.record_id}::${i}`;
                    return (
                      <label key={key} className="flex items-center gap-2 cursor-pointer bg-fuchsia-50/50 rounded-md px-2 py-1.5" data-testid={`scan-suggestion-${r.record_id}-${i}`}>
                        <input
                          type="checkbox"
                          checked={!!scanSelections[key]}
                          onChange={() => toggleScanSelection(key)}
                          className="accent-fuchsia-600 w-4 h-4"
                          data-testid={`scan-checkbox-${r.record_id}-${i}`}
                        />
                        <span className="text-sm font-medium flex-1">{s.name}{s.amount ? ` — $${Number(s.amount).toFixed(2)}` : ''}</span>
                        {s.evidence && <span className="text-xs text-slate-400 italic truncate max-w-[45%]">"{s.evidence}"</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setScanDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmScan} disabled={applyingScan} data-testid="scan-apply-confirm">
              {applyingScan ? 'Adding…' : 'Add Selected'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimesheetsPage;
